// Strong Typed Store

import { RegistrationForm } from '@axa-asia/emma-stylesheet/lib/components/EmmaOrganisms/EmmaPolicyInfoInput/types';
import { ContactEntry } from 'types';
import { PasswordForm } from 'types/password-form.type';
import { SelectedUsername } from 'types/select-username.type';
import type {
  RegistrationApiSuccessResp,
  FindMyPolicyConfigApiSuccessResp,
} from '@axa-asia/emma-prismic-api-client';
import { VerifiedCallbackPayload } from '@axa-asia/emma-otp';

export const PKG_NAME = '@axa-asia/emma-registration';

export type ScopedStore = {
  registrationConfig?: RegistrationApiSuccessResp['data'];
  findMyPolicyConfig?: FindMyPolicyConfigApiSuccessResp['data'];
  selectedPolicyType?: RegistrationForm['policyType'] | 'cwp';
  registrationForm?: RegistrationForm;
  usernameOptinos?: ContactEntry[];
  checkHash?: string;
  companyCode?: string;
  isAllowinsuredAccessFlag?: boolean;
  shouldSelectContactFlag?: boolean;
  shouldShowClaimSettleCheckbox?: boolean;
  selectedUsername?: SelectedUsername;
  otpPayload?: VerifiedCallbackPayload<'onboarding'>;
  passwordForm?: PasswordForm;
};

const initialStore: ScopedStore = {};

export type Store = {
  [PKG_NAME]: ScopedStore;
};

// Actions
export const SET_REGISTRATION_CONFIG = `${PKG_NAME}/SET_REGISTRATION_CONFIG`;
export const SET_FIND_MY_POLICY_CONFIG = `${PKG_NAME}/SET_FIND_MY_POLICY_CONFIG`;
export const SET_SELECTED_POLICY_TYPE = `${PKG_NAME}/SET_SELECTED_POLICY_TYPE`;
export const UPDATE_FORM_DATA = `${PKG_NAME}/UPDATE_FORM_DATA`;
export const CLEAR_FORM_DATA = `${PKG_NAME}/CLEAR_FORM_DATA`;
export const UPDATE_USERNAME_OPTIONS = `${PKG_NAME}/UPDATE_USERNAME_OPTIONS`;
export const CLEAR_USERNAME_OPTIONS = `${PKG_NAME}/CLEAR_USERNAME_OPTIONS`;
export const SET_CHECK_HASH = `${PKG_NAME}/SET_CHECK_HASH`;
export const SET_COMPANY_CODE = `${PKG_NAME}/SET_COMPANY_CODE`;
export const SET_REGISTRATION_FLAG = `${PKG_NAME}/SET_REGISTRATION_FLAG`;
export const SET_SELECTED_USERNAME = `${PKG_NAME}/SET_SELECTED_USERNAME`;
export const SET_OTP_PAYLOAD = `${PKG_NAME}/SET_OTP_PAYLOAD`;
export const SET_PASSWORD_FORM = `${PKG_NAME}/SET_PASSWORD_FORM`;
export const RESET_REGISTRATION_DATA = `${PKG_NAME}/RESET_STORE`;

export const setRegistrationConfig = (
  payload: RegistrationApiSuccessResp['data'],
) => ({
  type: SET_REGISTRATION_CONFIG,
  payload,
});

export const setFindMyPolicyConfig = (
  payload: FindMyPolicyConfigApiSuccessResp['data'],
) => ({
  type: SET_FIND_MY_POLICY_CONFIG,
  payload,
});

export const setSelectedPolicyType = (
  payload: ScopedStore['selectedPolicyType'],
) => ({
  type: SET_SELECTED_POLICY_TYPE,
  payload,
});

export const updateFormData = (payload: RegistrationForm) => ({
  type: UPDATE_FORM_DATA,
  payload,
});

export const clearFormData = () => ({
  type: CLEAR_FORM_DATA,
});

export const updateUsernameOptions = (payload: ContactEntry[]) => ({
  type: UPDATE_USERNAME_OPTIONS,
  payload,
});

export const clearUsernameOptions = () => ({
  type: CLEAR_USERNAME_OPTIONS,
});

export const setCheckHash = (payload: string) => ({
  type: SET_CHECK_HASH,
  payload,
});

export const setCompanyCode = (companyCode: string) => ({
  type: SET_COMPANY_CODE,
  payload: companyCode,
});

export const setSelectedUsername = (payload: SelectedUsername) => ({
  type: SET_SELECTED_USERNAME,
  payload,
});

export const resetRegistrationData = () => ({
  type: RESET_REGISTRATION_DATA,
});

export const setRegistrationFlag = (
  isAllowinsuredAccessFlag: boolean,
  shouldSelectContactFlag: boolean,
  shouldShowClaimSettleCheckbox: boolean,
) => ({
  type: SET_REGISTRATION_FLAG,
  payload: {
    isAllowinsuredAccessFlag,
    shouldSelectContactFlag,
    shouldShowClaimSettleCheckbox,
  },
});

export const setOtpPayload = (
  otpPayload: VerifiedCallbackPayload<'onboarding'>,
) => ({
  type: SET_OTP_PAYLOAD,
  payload: otpPayload,
});

export const setPasswordForm = (payload: PasswordForm) => ({
  type: SET_PASSWORD_FORM,
  payload,
});

// Reducer
const scopedRootReducer = (
  state: ScopedStore = initialStore,
  action,
): ScopedStore => {
  switch (action.type) {
    case SET_REGISTRATION_CONFIG:
      return { ...state, registrationConfig: action.payload };
    case SET_FIND_MY_POLICY_CONFIG:
      return { ...state, findMyPolicyConfig: action.payload };
    case SET_SELECTED_POLICY_TYPE:
      return {
        ...state,
        selectedPolicyType: action.payload,
      };
    case UPDATE_FORM_DATA:
      return {
        ...state,
        registrationForm: action.payload,
      };
    case CLEAR_FORM_DATA:
      return {
        ...state,
        registrationForm: undefined,
      };
    case UPDATE_USERNAME_OPTIONS:
      return {
        ...state,
        usernameOptinos: action.payload,
      };
    case CLEAR_USERNAME_OPTIONS:
      return {
        ...state,
        usernameOptinos: undefined,
      };
    case SET_CHECK_HASH:
      return { ...state, checkHash: action.payload };
    case SET_COMPANY_CODE:
      return { ...state, companyCode: action.payload };
    case SET_REGISTRATION_FLAG: {
      return { ...state, ...action.payload };
    }
    case SET_SELECTED_USERNAME:
      return { ...state, selectedUsername: action.payload };
    case SET_OTP_PAYLOAD:
      return { ...state, otpPayload: action.payload };
    case SET_PASSWORD_FORM:
      return { ...state, passwordForm: action.payload };
    case RESET_REGISTRATION_DATA:
      return {
        ...state,
        selectedPolicyType: undefined,
        registrationForm: undefined,
        usernameOptinos: undefined,
        checkHash: undefined,
        companyCode: undefined,
        isAllowinsuredAccessFlag: undefined,
        shouldSelectContactFlag: undefined,
        shouldShowClaimSettleCheckbox: undefined,
        selectedUsername: undefined,
        otpPayload: undefined,
        passwordForm: undefined,
      };
    default:
      return state;
  }
};

export const scopedReducers = {
  [PKG_NAME]: scopedRootReducer,
};
